import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';

import { FitDataService } from '../../training-analysis/fit-data.service';
import { OutlierListDialogComponent } from './outlier-list-dialog.component';

/**
 * Kompaktes globales Banner für Power-Datenausreißer.
 *
 * Zeigt nur den Hinweis + die Anzahl. Die Auflistung der betroffenen
 * Einheiten öffnet sich auf Klick als eigenes Overlay
 * ({@link OutlierListDialogComponent}).
 */
@Component({
  selector: 'app-outlier-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatButtonModule, TranslateModule],
  template: `
    @if (visible) {
      <div class="ob-banner">
        <mat-icon class="ob-icon">warning</mat-icon>
        <div class="ob-text">
          <strong>{{ 'calendar.outlier.banner_title' | translate }}</strong>
          <span class="ob-body">
            {{ 'calendar.outlier.banner_body' | translate:{ n: count } }}
          </span>
        </div>
        <span class="grow"></span>
        <button mat-flat-button color="primary" (click)="openList()">
          {{ 'calendar.outlier.cta' | translate }}
        </button>
        <button mat-icon-button (click)="dismiss()" aria-label="dismiss">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    }
  `,
  styles: [`
    .ob-banner {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.6rem 0.85rem;
      margin: 0.5rem 0;
      background: #fff3e0;
      color: #e65100;
      border-left: 4px solid #fb8c00;
      border-radius: 6px;
      font-size: var(--fs-sm);
    }
    .ob-icon { color: #fb8c00; flex-shrink: 0; }
    .ob-text { min-width: 0; }
    .ob-text strong { display: block; }
    .ob-body { display: block; margin-top: 0.1rem; }
    .grow { flex: 1; }
  `],
})
export class OutlierBannerComponent implements OnInit {
  private readonly fitData = inject(FitDataService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  count = 0;
  private suppressed = false;

  get visible(): boolean {
    return !this.suppressed && this.count > 0;
  }

  ngOnInit(): void {
    this.refresh();
  }

  private refresh(): void {
    this.fitData.getOutlierStatus().subscribe({
      next: (s) => { this.count = s?.count ?? 0; this.cdr.markForCheck(); },
      error: () => { this.count = 0; this.cdr.markForCheck(); },
    });
  }

  openList(): void {
    this.dialog.open(OutlierListDialogComponent, {
      width: '32rem',
      maxWidth: '95vw',
      maxHeight: '85dvh',
    }).afterClosed().subscribe(() => {
      // Sessions may have been corrected — refresh the count.
      this.refresh();
    });
  }

  dismiss(): void {
    this.suppressed = true;
    this.cdr.markForCheck();
  }
}
